import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from 'react'
import { listingsService } from '../services/listingsService'
import type {
  Listing,
  ListingFilters,
  ListingSortBy,
  AnalysisData,
} from '@popety-io/types'

interface ListingsContextState {
  listings: Listing[]
  loading: boolean
  error: string | null
  selectedListing: Listing | null
  filters: ListingFilters
  sortBy: ListingSortBy
  total: number
  currentPage: number
  hasMore: boolean
  viewMode: 'list' | 'detail'
  type: 'rent' | 'purchase'
}

interface ListingsContextValue extends ListingsContextState {
  searchListings: (page?: number, append?: boolean) => Promise<void>
  loadMore: () => Promise<void>
  selectListing: (listing: Listing | null) => void
  updateFilters: (filters: ListingFilters) => void
  clearFilters: () => void
  updateSort: (sortBy: ListingSortBy) => void
  setViewMode: (mode: 'list' | 'detail') => void
  retrySearch: () => Promise<void>
  getLocationText: () => string
  getTypeText: () => string
}

const ListingsContext = createContext<ListingsContextValue | undefined>(
  undefined
)

export const useListings = () => {
  const context = useContext(ListingsContext)
  if (!context) {
    throw new Error('useListings must be used within ListingsProvider')
  }
  return context
}

interface ListingsProviderProps {
  children: React.ReactNode
  type: 'rent' | 'purchase'
  analysisData?: AnalysisData | null
  propertyCoordinates?: [number, number]
  initialFilters?: Partial<ListingFilters>
}

export const ListingsProvider: React.FC<ListingsProviderProps> = ({
  children,
  type,
  propertyCoordinates,
  initialFilters = {},
}) => {
  const defaultFilters: ListingFilters = {
    deal_type: type,
    radius: 500,
    municipality: [],
    district: [],
    property_type: [],
    property_category: [],
    ...initialFilters,
  }

  const [state, setState] = useState<ListingsContextState>({
    listings: [],
    loading: false,
    error: null,
    selectedListing: null,
    filters: defaultFilters,
    sortBy: 'recent',
    total: 0,
    currentPage: 1,
    hasMore: false,
    viewMode: 'list',
    type,
  })

  const pageSize = 20

  // Search listings with current filters
  const searchListings = useCallback(
    async (page: number = 1) => {
      setState((prev) => ({ ...prev, loading: true, error: null }))

      try {
        const searchParams = {
          filters: { ...state.filters, deal_type: type }, // Ensure deal_type matches
          sort_by: state.sortBy,
          page,
          page_size: pageSize,
          property_coordinates: propertyCoordinates,
        }

        const result = await listingsService.search(searchParams)

        setState((prev) => ({
          ...prev,
          listings: result.listings,
          total: result.total,
          hasMore: result.has_more,
          currentPage: page,
          loading: false,
        }))
      } catch (err) {
        console.error('Error searching listings:', err)
        const errorMessage = 'Failed to load listings'

        setState((prev) => ({
          ...prev,
          error: errorMessage,
          loading: false,
        }))

        // Fallback to sample data for development
      }
    },
    [state.filters, state.sortBy, propertyCoordinates, type]
  )

  // Load more listings
  const loadMore = useCallback(async () => {
    if (!state.loading && state.hasMore) {
      await searchListings(state.currentPage + 1, true)
    }
  }, [searchListings, state.loading, state.hasMore, state.currentPage])

  // Select listing for detail view
  const selectListing = useCallback(async (listing: Listing | null) => {
    if (listing) {
      // Try to load full listing details
      try {
        const fullListing = await listingsService.getById(listing.id)

        if (fullListing) {
          setState((prev) => ({
            ...prev,
            selectedListing: fullListing,
            viewMode: 'detail',
          }))
        } else {
          setState((prev) => ({
            ...prev,
            selectedListing: listing,
            viewMode: 'detail',
          }))
        }
      } catch (err) {
        console.error('Error loading listing details:', err)
        // Use the original listing data as fallback
        setState((prev) => ({
          ...prev,
          selectedListing: listing,
          viewMode: 'detail',
        }))
      }
    } else {
      setState((prev) => ({
        ...prev,
        selectedListing: null,
        viewMode: 'list',
      }))
    }
  }, [])

  // Update filters
  const updateFilters = useCallback(
    (filters: ListingFilters) => {
      setState((prev) => ({
        ...prev,
        filters: { ...filters, deal_type: type }, // Ensure deal_type is preserved
        currentPage: 1, // Reset to first page when filters change
      }))
    },
    [type]
  )

  // Clear filters
  const clearFilters = useCallback(() => {
    setState((prev) => ({
      ...prev,
      filters: defaultFilters,
      currentPage: 1,
    }))
  }, [])

  // Update sort
  const updateSort = useCallback((sortBy: ListingSortBy) => {
    setState((prev) => ({
      ...prev,
      sortBy,
      currentPage: 1, // Reset to first page when sort changes
    }))
  }, [])

  // Set view mode
  const setViewMode = useCallback((mode: 'list' | 'detail') => {
    setState((prev) => ({ ...prev, viewMode: mode }))
  }, [])

  // Retry search
  const retrySearch = useCallback(async () => {
    await searchListings(1)
  }, [searchListings])

  // Get location display text
  const getLocationText = useCallback(() => {
    if (state.filters.municipality && state.filters.municipality.length > 0) {
      return state.filters.municipality.join(', ')
    }
    if (state.filters.district && state.filters.district.length > 0) {
      return state.filters.district.join(', ')
    }
    if (state.filters.radius) {
      return `within ${state.filters.radius}m`
    }
    return 'within 500m'
  }, [state.filters])

  // Get type display text
  const getTypeText = useCallback(() => {
    return type === 'rent' ? 'rental listings' : 'listings for sale'
  }, [type])

  // Search when filters or sort changes
  useEffect(() => {
    searchListings(1)
  }, [state.filters, state.sortBy])

  const value: ListingsContextValue = {
    ...state,
    searchListings,
    loadMore,
    selectListing,
    updateFilters,
    clearFilters,
    updateSort,
    setViewMode,
    retrySearch,
    getLocationText,
    getTypeText,
  }

  return (
    <ListingsContext.Provider value={value}>
      {children}
    </ListingsContext.Provider>
  )
}

export default ListingsProvider
